import Container from '@mui/material/Container';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid2';
import { INDEX_TO_COLOR } from '../../../data';
import { MemoizedFilterTabs } from '../../../components/base';
import { useAppSelector } from '../../../hooks/useAppStore';
import { getCommissionTeams } from '../../../store/commissions/selectors';
import { useTeamCommissions } from '../../commissions/hooks/useTeamCommissions';
import {
  AllTeamsAnalytics, SingleTeamAnalytics, TeamTotalsCard,
} from './components';
import { getSheets } from '../../../store/sheets/selectors';
import { TSingleTeam } from '../../../types';

const TeamAnalyticsView = () => {
  const { t } = useTranslation(['orders', 'dashboard', 'common', 'inputs', 'commissions']);
  const commissionTeams = useAppSelector(getCommissionTeams);
  const sheets = useAppSelector(getSheets);

  const {
    calculateTeamStepsCommission,
    calculateSplitCommission,
    calculateDuplicateCommission,
    aggregateTeamOrders,
  } = useTeamCommissions();

  const [filterStatus, setFilterStatus] = useState(0);

  const hoursByEmail = useMemo(() => {
    const hoursMap: Record<string, number> = {};

    sheets.forEach((sheet) => {
      const email = sheet.personnel.email?.toLowerCase();
      const totalHours = parseFloat(sheet.totals?.sheets?.totalHours || '0');

      if (email) {
        if (!hoursMap[email]) hoursMap[email] = 0;
        hoursMap[email] += totalHours;
      }
    });

    return hoursMap;
  }, [sheets]);

  const tabs = useMemo(() => {
    const baseTabs = commissionTeams.map((team, index) => ({
      value: team.id,
      label: team.teamName,
      color: INDEX_TO_COLOR[index],
      count: 0,
    }));

    baseTabs.unshift({
      value: 0,
      label: t('all', { ns: 'common' }),
      color: 'info',
      count: commissionTeams.length,
    });

    return baseTabs;
  }, [commissionTeams]);

  const handleFilterStatus = useCallback((newValue: number) => {
    setFilterStatus(newValue);
  }, []);

  const teamStats = useMemo(() => commissionTeams.map((team) => {
    const teamOrders = team.commissionModel.compensation === 'team steps'
    && team.commissionModel.goalsBase === 'location'
      ? aggregateTeamOrders
      : aggregateTeamOrders?.filter((order) => order.teamId === team.id) || [];

    const netSales = teamOrders.reduce((sum, order) => sum + order.summary.netSales, 0);
    const totalOrders = teamOrders.length;
    const productsSold = teamOrders.reduce((sum, order) => sum + (order.lineItems?.length || 0), 0);

    let commissionData: any;
    switch (team.commissionModel?.compensation) {
      case 'team steps':
        commissionData = calculateTeamStepsCommission(team, teamOrders, netSales);
        break;
      case 'split':
        commissionData = calculateSplitCommission(team, teamOrders, netSales);
        break;
      case 'duplicate':
        commissionData = calculateDuplicateCommission(teamOrders, netSales);
        break;
      default:
        commissionData = { netSales, commission: 0, employeeCommissions: {} };
    }

    const members = team.members.map((member) => {
      const email = member.email?.toLowerCase();
      const memberCommission = commissionData.employeeCommissions?.[email] || 0;
      const memberOrders = teamOrders.filter(
        (order) => String(order.userId) === String(member.shopifyId)
          || (Array.isArray(order.tags)
            && Array.isArray(member.commissionTags)
            && order.tags.some((tag) => member.commissionTags?.some((t) => t.toLowerCase() === tag.toLowerCase()))),
      );

      const memberNetSales = memberOrders.reduce((sum, o) => sum + o.summary.netSales, 0);
      const totalHoursWorked = email ? hoursByEmail[email] || 0 : 0;

      return {
        name: member.name,
        email: member.email,
        netSales: memberNetSales,
        commission: memberCommission,
        totalHoursWorked,
      };
    });

    return {
      id: team.id,
      name: team.teamName,
      model: team.commissionModel?.compensation,
      netSales,
      commission: commissionData.commission,
      memberCount: team.members.length,
      totalOrders,
      productsSold,
      members,
    };
  }), [commissionTeams, aggregateTeamOrders, hoursByEmail]);

  const selectedTeam = teamStats.find((team) => team.id === filterStatus);

  return (
    <Container
      sx={{
        px: { xs: 1, lg: 2 },
      }}
      maxWidth={false}
    >
      <MemoizedFilterTabs isDisplayBackground={false} tabs={tabs} onFilterChange={handleFilterStatus} />
      <Grid
        pt={2}
        size={{
          sm: 12, xs: 12, md: 12, lg: 12,
        }}
      >
        <TeamTotalsCard selectedTeamId={filterStatus} teamStats={teamStats} />
      </Grid>
      <Grid
        pt={4}
        size={{
          sm: 12, xs: 12, md: 12, lg: 12,
        }}
      >
        {filterStatus === 0 ? (
          <AllTeamsAnalytics teamStats={teamStats} />
        ) : (
          <SingleTeamAnalytics team={selectedTeam as TSingleTeam['team']} />
        )}
      </Grid>
    </Container>
  );
};

export {
  TeamAnalyticsView,
};
